import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { IConfig, IStatsListParams } from '@feathr/blackbox';
import { AggregatedStat, CampaignClass, CampaignState, reportModuleLabels } from '@feathr/blackbox';
import { moment } from '@feathr/hooks';
import { useStore } from '@feathr/report_components/state';

import type { IReportProps } from '../../types';
import ActivityCard from '../ActivityCard';
import ActivitySummaryCard from '../ActivitySummaryCard';
import AdPerformanceCard from '../AdPerformanceCard';
import BudgetCard from '../BudgetCard';
import ConversionTableCard from '../ConversionTableCard';
import EmailPerformanceCard from '../EmailPerformanceCard';
import LoadingMessage from '../LoadingMessage';
import { ReportContext } from '../Report.context';
import { ROICard } from '../ROICard';

function EventReport({
  config = {
    attributionModel: 'full',
    includeActivitySummary: true,
    includeAdActivity: true,
    includeAdPerformance: true,
    includeConversionsTable: false,
    includeEmailActivity: true,
    includeEmailPerformance: true,
    includePageActivity: true,
    includeROI: false,
    includeSpendPerformance: true,
  } as IConfig,
  end,
  localUrl,
  hasGoals,
  modelId,
  start,
}: Readonly<IReportProps>): JSX.Element {
  const { t } = useTranslation();
  return (
    <Observer>
      {function useAnonymousFunction(): JSX.Element {
        const { AggregatedStats, Events, Campaigns, Targetings, Goals, Flights, Stats } =
          useStore();

        const { prioritizeVerifiedActivity, setIsLoading } = useContext(ReportContext) ?? {};
        const pageSize =
          Math.max(
            Math.ceil(
              moment
                .duration(moment.utc(end, moment.ISO_8601).diff(moment.utc(start, moment.ISO_8601)))
                .asDays(),
            ),
            0,
          ) + 1;

        const event = Events.get(modelId);
        let eventDailyStats = Stats.newListResponse();
        const campaigns = Campaigns.list({
          filters: {
            _parent: modelId,
            is_archived__ne: true,
            state__ne: CampaignState.Draft,
            _cls__ne: CampaignClass.PinpointPartnerMessage,
          },
          pagination: {
            page_size: pageSize,
          },
        });
        const flights = Flights.list({
          filters: {
            event_id: modelId,
            is_archived__ne: true,
          },
        });
        if (!event.isPending) {
          const fetchParams: Partial<IStatsListParams> = {
            filters: {
              metadata__date__gte: start,
              metadata__date__lte: end,
              metadata__obj_id: modelId,
            },
            model: 'event',
            ordering: ['metadata.date'],
            pagination: {
              page_size: pageSize,
            },
          };

          if (!config.includeROI) {
            fetchParams.exclude = [
              'conversions.first_touch.segments_breakdown',
              'conversions.last_touch.segments_breakdown',
              'conversions.full.segments_breakdown',
              'conversions.linear.segments_breakdown',
              'conversions.positional.segments_breakdown',
              'conversions.temporal.segments_breakdown',
            ];
          }
          eventDailyStats = Stats.list(fetchParams, {});
        }
        let goals = Goals.newListResponse();
        let targetings = Targetings.newListResponse();
        if (!campaigns.isPending && !flights.isPending) {
          const flightlessCampaigns = campaigns.models.filter((cpn) => !cpn.get('flight'));
          goals = Goals.list({
            filters: {
              _parent__in: flights.models
                .map((f) => f.id)
                .concat(flightlessCampaigns.map((cpn) => cpn.id)),
              is_archived__ne: true,
            },
          });
          targetings = Targetings.list({
            filters: {
              _parent__in: campaigns.models.map((cpn) => cpn.id),
              is_archived__ne: true,
            },
          });
        }

        const aggregatedStats = AggregatedStats.aggregate({
          filters: {
            metadata__date__gte: start,
            metadata__date__lte: end,
            metadata__obj_id: modelId,
          },
          model: 'event',
        });

        const effectiveStats = prioritizeVerifiedActivity
          ? aggregatedStats
          : new AggregatedStat({
              ...aggregatedStats.toJS(),
              ...aggregatedStats.get('unfiltered', {}),
            });

        const pending =
          event.isPending ||
          eventDailyStats.isPending ||
          campaigns.isPending ||
          goals.isPending ||
          targetings.isPending ||
          aggregatedStats.isPending;

        useEffect(() => {
          setIsLoading?.(pending);
        }, [pending, setIsLoading]);

        if (pending) {
          return <LoadingMessage />;
        }

        const budget = campaigns.models
          .filter((cpn) => {
            const reportStartMoment = moment.utc(start, moment.ISO_8601);
            const reportEndMoment = moment.utc(end, moment.ISO_8601);
            const dateStartMoment = moment.utc(cpn.get('date_start'), moment.ISO_8601);
            return (
              dateStartMoment.isSameOrAfter(reportStartMoment) &&
              dateStartMoment.isBefore(reportEndMoment)
            );
          })
          .reduce((acc, cpn) => {
            const exposureSettings = cpn.get('exposure_settings');
            if (exposureSettings.target_type === 'fixed_budget') {
              return acc + (exposureSettings.target_value || 0);
            }
            return acc;
          }, 0);

        const showVerifiedActivityToggle = campaigns.models.some((cpn) =>
          cpn.get('show_bot_toggle'),
        );

        const numConversions =
          aggregatedStats.get('conversions')?.[config.attributionModel]?.num || 0;
        return (
          <>
            {config.includeROI && hasGoals && (
              <ROICard
                aggregatedStats={aggregatedStats}
                attributionModel={config.attributionModel}
                context={'project'}
                goals={goals.models.map((goal) => goal.toJS())}
              />
            )}
            {config.includeActivitySummary && (
              <ActivitySummaryCard
                aggregatedStats={effectiveStats}
                isEventOrFlight={true}
                reachTooltipText={t(
                  'People who viewed marketing material from campaigns in this project.',
                )}
                uniqueClicksTooltipText={t(
                  'Unique people who clicked on marketing material from campaigns in this project.',
                )}
              />
            )}
            {config.includeAdPerformance && (
              <AdPerformanceCard
                aggregatedStats={effectiveStats}
                clicksTooltip={t('Total clicks across all ad campaigns in this project.')}
                viewsTooltip={t('Total views across all ad campaigns in this project.')}
              />
            )}
            {config.includeEmailPerformance && (
              <EmailPerformanceCard
                aggregatedStats={effectiveStats}
                reportType={'Project'}
                showVerifiedActivityToggle={showVerifiedActivityToggle}
              />
            )}
            {config.includeSpendPerformance && (
              <BudgetCard
                aggregatedStats={effectiveStats}
                budget={budget}
                conversions={numConversions}
                spendTooltip={t(
                  'Total spend for all campaigns that are in this project and within the report date window.',
                )}
              />
            )}
            <div style={{ marginBottom: '10px' }}>
              {config.includeAdActivity && (
                <ActivityCard
                  dailyStats={eventDailyStats.models}
                  label={reportModuleLabels.includeAdActivity}
                  lines={[
                    { name: 'Views', statsKey: 'num_views_ad' },
                    { name: 'Clicks', statsKey: 'num_clicks_ad', yAxisId: 'right' },
                  ]}
                />
              )}
              {config.includeEmailActivity && (
                <ActivityCard
                  dailyStats={eventDailyStats.models}
                  label={reportModuleLabels.includeEmailActivity}
                  lines={[
                    { name: 'Views', statsKey: 'num_views_email' },
                    { name: 'Clicks', statsKey: 'num_clicks_email', yAxisId: 'right' },
                  ]}
                />
              )}
              {config.includePageActivity && (
                <ActivityCard
                  dailyStats={eventDailyStats.models}
                  label={reportModuleLabels.includePageActivity}
                  lines={[
                    { name: 'Views', statsKey: 'num_views_page' },
                    { name: 'Clicks', statsKey: 'num_clicks_page', yAxisId: 'right' },
                  ]}
                />
              )}
              {config.includeConversionsTable && hasGoals && (
                <ConversionTableCard.V1
                  attributionModel={config.attributionModel}
                  end={end}
                  goals={goals.models.map((goal) => goal.toJS())}
                  key={`${modelId}-${start}-${end}-${config.attributionModel}`}
                  localUrl={localUrl}
                  model={event}
                  start={start}
                  targetings={targetings.models}
                />
              )}
            </div>
          </>
        );
      }}
    </Observer>
  );
}

export default EventReport;
